import React, { FC, useLayoutEffect, useMemo, useState } from 'react';
import {
  AnimatedEmoji,
  Button,
  Header,
  List,
  ListItem,
  LottieIcon,
  Placeholder,
  ScrollView,
} from '$uikit';
import { IcLogo40, IcSubscriptionMedia28 } from '$assets';
import { LoadingStatus } from '$store/reels/interface';
import { Api } from '$api';
import { useDispatch } from 'react-redux';
import { useTranslator } from '$hooks';
import { toastActions } from '$store/toast';
import { Loader } from '$shared/components';
import { CoinBoostsTask } from '$store/models';
import { INVITE, MINE } from '$shared/constants/pages';
import router from '$navigation/router';
import { prettifyNumber } from '$utils/number';
import { mainActions } from '$store/main';
import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';
import s from './MineBoost.module.sass';
import { classNames, format } from '$utils';

const DailyBonus: FC<{ strike: number; lastVisitAt: number; loadData: () => void }> = ({
  strike,
  lastVisitAt,
  loadData,
}) => {
  const t = useTranslator();
  const dispatch = useDispatch();

  const canClaim = Date.now() / 1000 - lastVisitAt > 86400;

  const table = useMemo(() => {
    const result: { day: number; reward: number }[] = [];
    for (let i = 0; i < 7; i++) {
      const day = i + 1;
      result.push({
        day: day,
        reward: 500 * (1 << (day - 1)),
      });
    }
    return result;
  }, [strike, lastVisitAt]);

  const handleClaim = () => {
    if (!canClaim) {
      return;
    }

    dispatch(toastActions.setToastLoading());
    Api.post('/mine/boosts/daily_reward')
      .then(() => {
        dispatch(toastActions.setToastSuccess());
        dispatch(mainActions.setPopup(null));
        loadData();
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
      });
  };

  const handleDayPress = (day) => {
    const nextTs = lastVisitAt + day * 86400 - Math.max(0, strike - 1) * 86400;
    const date = format(nextTs * 1000, 'd MMMM yyyy');
    dispatch(toastActions.setToastSuccess(date));
  };

  return (
    <div className={s.wrap}>
      <Placeholder
        title={t('mine_boost_task_daily')}
        icon={<AnimatedEmoji emoji="💰" loop size={64} />}
        iconSize={64}
        compact
      >
        {t('mine_boost_task_daily_info')}
      </Placeholder>
      <div className={s.dailyBonusRows}>
        {table.map((item) => (
          <div
            key={item.day}
            className={classNames({
              [s.dailyBonusRow]: true,
              [s.dailyBonusRowInactive]: item.day > strike,
              [s.dailyBonusRowCurrent]: item.day === strike + 1 && canClaim,
              [s.dailyBonusRowClaimed]: strike >= item.day,
            })}
            onClick={() => handleDayPress(item.day - 1)}
          >
            <div className={s.dailyBonusRowInner}>
              <div className={s.dailyBonusRowDay}>
                {t('mine_boost_task_daily_info_day', {
                  day: item.day,
                })}
              </div>
              <IcLogo40 />
              <div className={s.dailyBonusReward}>{prettifyNumber(item.reward)}</div>
            </div>
          </div>
        ))}
      </div>
      <div className={s.buttonWrap}>
        <Button onClick={handleClaim} disabled={!canClaim}>
          {canClaim
            ? t('mine_boost_task_daily_claim')
            : t('mine_boost_task_daily_claim_tomorrow')}
        </Button>
      </div>
    </div>
  );
};

export const OldBonus: FC<{
  bonus: number;
  registerDays: number;
  loadData: () => void;
  claimed: boolean;
}> = ({ registerDays, loadData, bonus, claimed }) => {
  const dispatch = useDispatch();
  const t = useTranslator();

  const handleClaim = () => {
    dispatch(toastActions.setToastLoading());
    Api.post('/mine/boosts/task/activate', {
      task: 'old_bonus',
    })
      .then(() => {
        dispatch(toastActions.setToastSuccess());
        dispatch(mainActions.setPopup(null));
        loadData();
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
      });
  };

  return (
    <div className={s.wrap}>
      <Placeholder
        icon={<AnimatedEmoji emoji="🎉" size={120} loop />}
        title={t('mine_old_bonus_title', {
          count: registerDays,
        })}
        actions={
          <Button onClick={handleClaim} disabled={claimed}>
            {claimed ? t('mine_old_bonus_claimed') : t('mine_boost_task_daily_claim')}
          </Button>
        }
        iconSize={120}
        compact
      >
        {t('mine_old_bonus_caption', {
          bonus: prettifyNumber(bonus),
        })}
      </Placeholder>
    </div>
  );
};

export const MineBoost: FC = () => {
  const dispatch = useDispatch();
  const t = useTranslator();

  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>('loading');
  const [info, setInfo] = useState<{
    tasks: CoinBoostsTask[];
    lastVisitAt: number;
    strike: number;
    registerDays: number;
  } | null>(null);

  const tasks = useMemo(() => {
    if (!info) {
      return [];
    }

    const copy = [...info.tasks];
    copy.sort((a, b) => {
      if (a.claimed === b.claimed) {
        return 0;
      }
      return a.claimed ? 1 : -1;
    });
    return copy;
  }, [info]);

  const handleDailyBonus = () => {
    dispatch(
      mainActions.setPopup(
        <BottomSheet>
          <DailyBonus
            strike={info!.strike}
            lastVisitAt={info!.lastVisitAt}
            loadData={loadData}
          />
        </BottomSheet>,
      ),
    );
  };

  const loadData = () => {
    setLoadingStatus('loading');
    Api.get<any, any>('/mine/boosts')
      .then((resp) => {
        setInfo(resp);
        setLoadingStatus('');
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
        setLoadingStatus('failed');
      });
  };

  useLayoutEffect(() => {
    loadData();
  }, []);

  const handleTask = (task) => {
    if (task.id === 'old_bonus') {
      dispatch(
        mainActions.setPopup(
          <BottomSheet>
            <OldBonus
              registerDays={info!.registerDays}
              loadData={loadData}
              bonus={task.reward}
              claimed={task.claimed}
            />
          </BottomSheet>,
        ),
      );
      return;
    }

    dispatch(toastActions.setToastLoading());
    Api.post('/mine/boosts/task/activate', {
      task: task.id,
    })
      .then(() => {
        setInfo((prev) => {
          if (!prev) {
            return null;
          }

          const tasks = [...prev.tasks].filter((item) => item.id !== task.id);
          return {
            ...prev,
            tasks,
          };
        });

        dispatch(toastActions.setToastSuccess());
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
        if (e.code === 'not_completed') {
          if (task.id === 'invite_friends') {
            router.navigate(INVITE, { from: 'mine' });
          }
        }
      });
  };

  function renderContent() {
    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={<Button onClick={loadData}>{t('failed_button')}</Button>}
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (loadingStatus === 'loading' || !info) {
      return <Loader isWrapped />;
    }

    return (
      <>
        <Placeholder
          iconSize={120}
          icon={<LottieIcon name="paywall" loop />}
          compact
          title={t('mine_boost_placeholder_title')}
        />
        <div style={{ padding: 16 }}>
          <div className="SubscriptionAuthor__list">
            <div className="SubscriptionAuthor__item">
              <div className="SubscriptionAuthor__item__icon">
                <IcSubscriptionMedia28 />
              </div>
              <div className="SubscriptionAuthor__item__title">
                {t('mine_boost_subscription_title')}
              </div>
              <div className="SubscriptionAuthor__item__description">
                {t('mine_boost_subscription_text')}
              </div>
            </div>

            {/*<div className="SubscriptionAuthor__item">*/}
            {/*  <div className="SubscriptionAuthor__item__icon">*/}
            {/*    <IcSubscriptionDirect28 />*/}
            {/*  </div>*/}
            {/*  <div className="SubscriptionAuthor__item__title">Покупки у моделей</div>*/}
            {/*  <div className="SubscriptionAuthor__item__description">*/}
            {/*    Начисляем 10 монет за каждый потраченый евро.*/}
            {/*  </div>*/}
            {/*</div>*/}
          </div>
          <List
            title={t('mine_boost_tasks_title')}
            description={t('mine_boost_tasks_caption')}
          >
            <ListItem indicator="+99 500" chevron onClick={handleDailyBonus}>
              {t('mine_boost_task_daily')}
            </ListItem>
            {tasks.map((item) => {
              let href: any = undefined;
              if (item.id === 'follow_channel') {
                href = 'https://t.me/TonPlaceCoin';
              } else if (
                item.id === 'invite_friends' ||
                item.id === 'earn_10k_coins' ||
                item.id === 'sponsorship' ||
                item.id === 'earn_100k_coins'
              ) {
                // nothing to do
              } else if (item.id === 'old_bonus') {
                //
              } else {
                return null;
              }
              let target = href ? '_blank' : undefined;

              return (
                <ListItem
                  href={href}
                  target={target}
                  key={item.id}
                  indicator={`+${prettifyNumber(item.reward)}`}
                  onClick={() => handleTask(item)}
                  chevron
                >
                  {item.title}
                </ListItem>
              );
            })}
          </List>
        </div>
      </>
    );
  }

  return (
    <>
      <Header showBackButton>{t('mine_boost_title')}</Header>
      <ScrollView
        footer={
          <Button
            onClick={() => router.navigate(MINE, {}, { replace: true })}
            mode="light"
          >
            {t('mine_go_to_game')}
          </Button>
        }
      >
        {renderContent()}
      </ScrollView>
    </>
  );
};
